import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import '../Estudios';
import Popup from 'devextreme-react/popup';
import { useApiConfiguration } from '../../../api/api.configuration';
import { useScreenSize } from '../../../utils/media-query';
import DataGrid, { Column, Button as Butt, Editing, Item as Items, KeyboardNavigation, Paging, Scrolling, Sorting, StateStoring, Selection, FilterRow } from 'devextreme-react/data-grid';
import DropDownBox, { DropDownBoxTypes } from 'devextreme-react/drop-down-box';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { SysIAnalitos } from '../../../types/types.analitos';
import { Rpm_EstudiosAnalitos } from '../../../types/types.estudios';

export const ModalAnalytesByStudy = ({ visible, setVisible, IdEstudio, Estudio, Analitos }: { visible: boolean; setVisible: (value: boolean) => void; IdEstudio: number; Estudio: string; Analitos: SysIAnalitos[] }) => {
  const { getAnalytesByStudy, newStudyAnalyte, setStudyAnalyte, deleteStudyAnalyte } = useApiConfiguration();
  const { isMedium, isSmall } = useScreenSize();
  const [gridBoxValue, setGridBoxValue] = useState([2]);
  const [isGridBoxOpened, setIsGridBoxOpened] = useState(false);
  const [data, setData] = useState<SysIAnalitos[]>([]);
  // const [dataEstudiosAnalitos, setDataEstudiosAnalitos] = useState<Rpm_EstudiosAnalitos[]>([]);
  const ownerLabel = { 'aria-label': 'Owner' };
  const dataRef = useRef<DataGrid>(null);
  const dataRefs = useRef<DataGrid>(null);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Rea_Id',
      load: async () => {
        try {
          const res = await getAnalytesByStudy(IdEstudio);

          if (!Array.isArray(res)) return [];

          const ordenado = res.sort((a, b) => {
            const ordenA = a.Rea_OrdenImpresion ?? Number.MAX_SAFE_INTEGER;
            const ordenB = b.Rea_OrdenImpresion ?? Number.MAX_SAFE_INTEGER;
            return ordenA - ordenB;
          });

          return ordenado;
        } catch (error) {
          console.error('Error al cargar datos:', error);
          return [];
        }
      },
      update: (key, values) => {
        return setStudyAnalyte({ RpmAnalito: values, IdRpm: key })
          .then((res) => {
            if (res === 107) {
              dataRef.current?.instance.refresh();
            }
          })
          .catch((err) => {
            throw new Error(err);
          });
      },
      remove: (key) => {
        return deleteStudyAnalyte({ IdRpm: key }).then((res) => {
          if (res === 107) {
            dataRef.current?.instance.refresh();
          }
        });
      },
    });
  }, [IdEstudio]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleVisible = useCallback(() => {
    setVisible(false);
  }, []);

  const dataGridOnSelectionChanged = useCallback((e: { selectedRowKeys: any }) => {
    setGridBoxValue(e.selectedRowKeys);
    setIsGridBoxOpened(false);
  }, []);

  const handleConfigureClick = useCallback(
    (e) => {
      dataRefs.current?.instance.refresh();
      const { data }: { data: SysIAnalitos } = e.row;
      if (data) {
        const updatedDataRpm: Rpm_EstudiosAnalitos = {
          Rea_Id: 0,
          Rea_OrdenImpresion: 1,
          Rea_An_Id: data.An_Id,
          Rea_Es_Id: IdEstudio,
        };
        newStudyAnalyte({ RpmAnalito: updatedDataRpm }).then((res) => {
          if (res === 105) {
            dataRef.current?.instance.refresh();
          }
        });
        setIsGridBoxOpened(false);
      }
    },
    [data]
  );

  const dataGridRender = useCallback(() => {
    return (
      <div style={{ height: 300 }}>
        <DataGrid
          dataSource={Analitos?.length ? Analitos : []}
          ref={dataRefs}
          hoverStateEnabled={true}
          showBorders={true}
          selectedRowKeys={gridBoxValue}
          onSelectionChanged={dataGridOnSelectionChanged}
          height='100%' // ✅ tomará 300px del contenedor padre
        >
          <Scrolling mode='virtual' />
          <Selection mode='single' />
          <Paging enabled={true} />
          <FilterRow visible={true} />
          <Column name='An_Id' caption='ID' dataField='An_Id' width={100} alignment='center' />
          <Column name='An_Nombre' caption='ANALITO' dataField='An_Nombre' alignment='left' />
          <Column type='buttons' caption='ACCIÓN'>
            <Butt hint='Configurar' icon='plus' visible={true} disabled={false} onClick={handleConfigureClick} />
          </Column>
        </DataGrid>
      </div>
    );
  }, [Analitos, gridBoxValue, dataGridOnSelectionChanged]);

  const gridBoxDisplayExpr = (item: { An_Id: any; An_Nombre: any }) => item && `${item.An_Nombre} <${item.An_Id}>`;

  const handleSelectedData = useCallback((e: DropDownBoxTypes.ValueChangedEvent) => {
    setGridBoxValue(e.value);
  }, []);

  const onGridBoxOpened = useCallback((e: DropDownBoxTypes.OptionChangedEvent) => {
    if (e.name === 'opened') {
      setIsGridBoxOpened(e.value);
    }
  }, []);

  return (
    <Popup width={1200} height={'auto'} fullScreen={isMedium || isSmall} visible={visible} onHidden={handleVisible} title={IdEstudio + ' - ' + Estudio} showTitle={true} showCloseButton={true}>
      <div>
        <DropDownBox dataSource={data} value={gridBoxValue} opened={isGridBoxOpened} valueExpr={'An_Id'} displayExpr={gridBoxDisplayExpr} deferRendering={false} inputAttr={ownerLabel} placeholder='ANALITOS' showClearButton={true} onValueChanged={handleSelectedData} onOptionChanged={onGridBoxOpened} contentRender={dataGridRender} />
        <br></br>
        <DataGrid className='grid' dataSource={customDataSrc} ref={dataRef} hoverStateEnabled={true} height='auto' repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true}>
          <Scrolling columnRenderingMode='virtual' />
          <Sorting mode='multiple' />
          <Selection mode='single' />
          <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
          <Paging enabled={false} />
          <StateStoring enabled={true} type='custom' />
          <Editing mode='form' allowUpdating={true} allowDeleting={true} selectTextOnEditStart={true} texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar' }} />
          <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={true} dataField='Analito.An_Id' width={80} alignment='center' name='An_Id' />
          <Column allowGrouping={false} caption='ANALITO' visible={true} allowEditing={false} dataField='Analito.An_Nombre' alignment='left' />
          <Column allowGrouping={false} caption='ABREVIATURA' allowEditing={false} allowSorting={false} dataField='Analito.An_Abreviatura' width={120} alignment='center' />
          <Column allowGrouping={false} caption='ORDEN' allowEditing={true} allowSorting={false} dataField='Rea_OrdenImpresion' width={120} alignment='center' />
          <Column type='buttons' caption={'ACCIÓN'} width={150}>
            <Butt name='edit' />
            <Butt name='delete' />
          </Column>
        </DataGrid>
      </div>
    </Popup>
  );
};
